import React from 'react';
import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import {
  Text,
  Box,
  TextField,
  Callout,
  Code,
  Icon,
  Flex,
  Link,
  Card,
  Tooltip,
} from '@scale/scaleui-radix';
import { faBookOpen } from '@fortawesome/pro-solid-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { useCompassCard } from '@scale/compass-ui/store';
import type { ModalExecutionContext } from '@scale/genai-compass-shared/cards/modal_execution';
import { CardType } from '@scale/genai-compass-shared/cardTypes';
import { CompassInstructions } from '@scale/compass-ui/components/CardInstructions';

const ModalExecutionCardComponent: CompassCardComponent = ({
  index,
}: CompassCardComponentProps) => {
  const { card, updateCardContext, updateCardCanSubmit } = useCompassCard(index);

  const context: ModalExecutionContext = (card?.defaultContext as ModalExecutionContext) || {
    cardType: CardType.MODAL_EXECUTION,
    modalAppName: '',
    modalFunctionName: '',
  };

  const updateContext = (updates: Partial<ModalExecutionContext>) => {
    const updatedContext: ModalExecutionContext = {
      ...context,
      ...updates,
    };
    updateCardContext(updatedContext);

    const canSubmit = !!updatedContext.modalAppName && !!updatedContext.modalFunctionName;
    updateCardCanSubmit(canSubmit);
  };

  const setModalAppName = (value: string) => updateContext({ modalAppName: value });
  const setModalFunctionName = (value: string) => updateContext({ modalFunctionName: value });

  return (
    <Flex direction="column" gap="4" p="4">
      <CompassInstructions
        header="How to use the Modal Execution card"
        storageKey="modalExecutionCardInstructionsExpanded"
        icon={faBookOpen}
      >
        <Callout.Text>
          <Text as="p">
            Provide the Modal <Code>app name</Code> and <Code>function name</Code> to invoke for
            data transformation. The Modal function should have the signature{' '}
            <Code>(data: list[dict]) -&gt; list[dict]:</Code>. The function should return an array
            of transformed row objects that will become the next data table in the workflow.
          </Text>
          <Box pt="2">
            <Text as="p">
              See{' '}
              <Link
                href="https://modal.com/apps/scaleapi/main/deployed/compass-modal-demo?activeTab=overview"
                target="_blank"
                rel="noopener noreferrer"
              >
                this example Modal app
              </Link>
              , the{' '}
              <Link
                href="https://gist.github.com/ethan-jiang-scale/bafac6a16b9e9a77c0ec48b54a178e0c"
                target="_blank"
                rel="noopener noreferrer"
              >
                example code on GitHub
              </Link>
              , and the{' '}
              <Link href="https://modal.com/docs" target="_blank" rel="noopener noreferrer">
                Modal Documentation
              </Link>{' '}
              for reference.
            </Text>
          </Box>
        </Callout.Text>
      </CompassInstructions>

      <Card>
        <Flex direction="column" gap="2" p="2">
          <Flex align="center" gap="1">
            <Text as="p" size="2" weight="medium">
              Modal App Name
            </Text>
            <Tooltip
              content={
                <Text size="1">
                  Your Modal app name, e.g.{' '}
                  <Code variant="solid">app = modal.App("compass-modal-demo")</Code> would be
                  "compass-modal-demo"
                </Text>
              }
            >
              <Icon icon={faCircleInfo} size="1" label="Info" color="neutral" />
            </Tooltip>
          </Flex>
          <Box maxWidth="400px">
            <TextField.Root
              placeholder="compass-modal-demo"
              value={context.modalAppName || ''}
              onChange={e => setModalAppName(e.target.value)}
              size="2"
            />
          </Box>
          <Flex align="center" gap="1">
            <Text as="p" size="2" weight="medium">
              Modal Function Name
            </Text>
            <Tooltip
              content={
                <Text size="1">
                  The Function in the Modal app you want to invoke, e.g.{' '}
                  <Code variant="solid">@app.function ... def modal_transform</Code> would be
                  "modal_transform"
                </Text>
              }
            >
              <Icon icon={faCircleInfo} size="1" label="Info" color="neutral" />
            </Tooltip>
          </Flex>
          <Box maxWidth="400px">
            <TextField.Root
              placeholder="modal_transform"
              value={context.modalFunctionName || ''}
              onChange={e => setModalFunctionName(e.target.value)}
              size="2"
            />
          </Box>
        </Flex>
      </Card>
    </Flex>
  );
};

export default ModalExecutionCardComponent;
